import React, { useState, useEffect, useCallback } from 'react';
import { accountingApi, projectApi } from '../services/api';
import { Button, Input, Select, Card, Modal, Badge } from '../components/UI';
import { BookOpen, Plus, Trash, CaretDown, CaretRight, Scales, ChartLine, Bank, Receipt } from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt = (v) => new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export default function AccountingPage() {
  const [tab, setTab] = useState('coa');
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selProject, setSelProject] = useState('');
  const [ledger, setLedger] = useState([]);
  const [trialBalance, setTrialBalance] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [showAccModal, setShowAccModal] = useState(false);
  const [accForm, setAccForm] = useState({ code: '', name: '', account_type: 'asset', parent_code: '', description: '' });
  const [error, setError] = useState('');
  const [selAccount, setSelAccount] = useState('');

  const load = useCallback(async () => {
    try {
      const [accRes, projRes] = await Promise.all([accountingApi.listAccounts(), projectApi.list()]);
      setAccounts(accRes.data);
      setProjects(projRes.data);
    } catch (e) { setError('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadTabData = useCallback(async () => {
    const params = {};
    if (selProject) params.project_id = selProject;
    try {
      if (tab === 'general-ledger') {
        if (selAccount) params.account = selAccount;
        const res = await accountingApi.generalLedger(params);
        setLedger(res.data);
      } else if (tab === 'trial-balance') {
        const res = await accountingApi.trialBalance(params);
        setTrialBalance(res.data);
      } else if (tab === 'pnl') {
        const res = await accountingApi.profitLoss(params);
        setPnl(res.data);
      } else if (tab === 'balance-sheet') {
        const res = await accountingApi.balanceSheet(params);
        setBs(res.data);
      }
    } catch (e) { setError('Failed to load data'); }
  }, [tab, selProject, selAccount]);

  useEffect(() => { if (tab !== 'coa') loadTabData(); }, [loadTabData, tab]);

  const handleSeedCoa = async () => {
    try { await accountingApi.seedCoa(); load(); } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateAcc = async (e) => {
    e.preventDefault(); setError('');
    try { await accountingApi.createAccount(accForm); setShowAccModal(false); setAccForm({ code: '', name: '', account_type: 'asset', parent_code: '', description: '' }); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const exportPDF = (title, headers, rows) => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(title, 14, 20);
    if (selProject) { const pname = projects.find(p => p.id === selProject)?.name || ''; doc.setFontSize(10); doc.text(`Project: ${pname}`, 14, 28); }
    doc.setFontSize(8); doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, selProject ? 34 : 28);
    autoTable(doc, { startY: selProject ? 38 : 32, head: [headers], body: rows, styles: { fontSize: 8 } });
    doc.save(`${title.replace(/\s/g, '_').toLowerCase()}.pdf`);
  };

  const typeColors = { asset: '#002FA7', liability: '#FF3333', equity: '#7C3AED', revenue: '#00C853', expense: '#FFAB00' };
  const tabs = [
    { key: 'coa', label: 'Chart of Accounts', icon: BookOpen },
    { key: 'general-ledger', label: 'General Ledger', icon: Receipt },
    { key: 'trial-balance', label: 'Trial Balance', icon: Scales },
    { key: 'pnl', label: 'Profit & Loss', icon: ChartLine },
    { key: 'balance-sheet', label: 'Balance Sheet', icon: Bank },
  ];

  return (
    <div className="space-y-4" data-testid="accounting-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A]" data-testid="accounting-heading">Accounting Ledgers</h1>
        <p className="text-sm text-[#666]">Double-entry bookkeeping — CoA, General Ledger, Trial Balance, P&L, Balance Sheet</p>
      </div>

      <div className="flex gap-1 border-b border-[#E0E0E0] overflow-x-auto" data-testid="acc-tabs">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? 'border-[#1A1F36] text-[#1A1F36]' : 'border-transparent text-[#888] hover:text-[#555]'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab !== 'coa' && (
        <Select value={selProject} onChange={e => setSelProject(e.target.value)} className="max-w-xs" label="Filter by Project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      )}

      {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}

      {/* Chart of Accounts */}
      {tab === 'coa' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#666]">{accounts.length} account(s)</span>
            <div className="flex gap-2">
              {accounts.length === 0 && <Button size="sm" variant="secondary" onClick={handleSeedCoa} data-testid="seed-coa-btn">Seed Default CoA</Button>}
              <Button size="sm" onClick={() => setShowAccModal(true)} data-testid="add-acc-btn"><Plus className="w-4 h-4 mr-1" /> Add Account</Button>
            </div>
          </div>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                  <th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Parent</th><th className="px-4 py-2.5"></th>
                </tr></thead>
                <tbody>
                  {accounts.map(a => (
                    <tr key={a.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`coa-row-${a.code}`}>
                      <td className="px-4 py-2 font-mono text-[#002FA7]">{a.code}</td>
                      <td className="px-4 py-2 font-medium text-[#0A0A0A]">{a.name}</td>
                      <td className="px-4 py-2"><Badge style={{ color: typeColors[a.account_type] || '#666' }}>{a.account_type}</Badge></td>
                      <td className="px-4 py-2 text-[#666]">{a.parent_code || '-'}</td>
                      <td className="px-4 py-2"><button onClick={async () => { if (window.confirm('Delete?')) { await accountingApi.deleteAccount(a.id); load(); } }} className="p-1 text-[#666] hover:text-[#FF3333]"><Trash className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* General Ledger */}
      {tab === 'general-ledger' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Select value={selAccount} onChange={e => setSelAccount(e.target.value)} className="max-w-xs" label="Filter by Account">
              <option value="">All Accounts</option>
              {[...new Set(ledger.map(l => l.account))].sort().map(a => <option key={a} value={a}>{a}</option>)}
            </Select>
            <Button size="sm" variant="secondary" onClick={() => exportPDF('General Ledger', ['Date', 'Description', 'Account', 'Debit', 'Credit'], ledger.map(l => [l.date?.split('T')[0], l.description, l.account, fmt(l.debit), fmt(l.credit)]))} data-testid="export-gl-pdf">Export PDF</Button>
          </div>
          {ledger.length === 0 ? <Card className="p-8 text-center text-[#666]">No journal entries</Card> : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                    <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Description</th>
                    <th className="px-4 py-2.5">Account</th><th className="px-4 py-2.5">Ref</th>
                    <th className="px-4 py-2.5 text-right">Debit</th><th className="px-4 py-2.5 text-right">Credit</th>
                  </tr></thead>
                  <tbody>
                    {ledger.slice(0, 200).map((l, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                        <td className="px-4 py-2 text-[#666]">{l.date?.split('T')[0]}</td>
                        <td className="px-4 py-2 text-[#0A0A0A]">{l.description}</td>
                        <td className="px-4 py-2 font-medium text-[#002FA7]">{l.account}</td>
                        <td className="px-4 py-2 text-[10px] text-[#999]">{l.reference_type}</td>
                        <td className="px-4 py-2 text-right">{l.debit > 0 ? fmt(l.debit) : '-'}</td>
                        <td className="px-4 py-2 text-right">{l.credit > 0 ? fmt(l.credit) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Trial Balance */}
      {tab === 'trial-balance' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => exportPDF('Trial Balance', ['Account', 'Debit', 'Credit', 'Balance'], trialBalance.map(t => [t.account, fmt(t.total_debit), fmt(t.total_credit), fmt(t.balance)]))} data-testid="export-tb-pdf">Export PDF</Button>
          </div>
          {trialBalance.length === 0 ? <Card className="p-8 text-center text-[#666]">No data for trial balance</Card> : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                    <th className="px-4 py-2.5">Account</th>
                    <th className="px-4 py-2.5 text-right">Debit</th><th className="px-4 py-2.5 text-right">Credit</th>
                    <th className="px-4 py-2.5 text-right">Balance</th>
                  </tr></thead>
                  <tbody>
                    {trialBalance.map((t, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`tb-row-${t.account}`}>
                        <td className="px-4 py-2 font-medium text-[#0A0A0A]">{t.account}</td>
                        <td className="px-4 py-2 text-right text-[#666]">{t.total_debit > 0 ? fmt(t.total_debit) : '-'}</td>
                        <td className="px-4 py-2 text-right text-[#666]">{t.total_credit > 0 ? fmt(t.total_credit) : '-'}</td>
                        <td className={`px-4 py-2 text-right font-bold ${t.balance > 0 ? 'text-[#002FA7]' : t.balance < 0 ? 'text-[#FF3333]' : ''}`}>{fmt(t.balance)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-[#0A0A0A] font-bold">
                      <td className="px-4 py-2 text-[#0A0A0A]">Total</td>
                      <td className="px-4 py-2 text-right">{fmt(trialBalance.reduce((s, t) => s + t.total_debit, 0))}</td>
                      <td className="px-4 py-2 text-right">{fmt(trialBalance.reduce((s, t) => s + t.total_credit, 0))}</td>
                      <td className="px-4 py-2 text-right">{fmt(trialBalance.reduce((s, t) => s + t.balance, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Profit & Loss */}
      {tab === 'pnl' && pnl && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = [...pnl.revenue.map(r => ['Revenue', r.account, fmt(r.amount)]), ['', 'Total Revenue', fmt(pnl.total_revenue)],
                ...pnl.expenses.map(r => ['Expense', r.account, fmt(r.amount)]), ['', 'Total Expenses', fmt(pnl.total_expenses)],
                ['', 'Net Profit/Loss', fmt(pnl.net_profit)]];
              exportPDF('Profit & Loss', ['Category', 'Account', 'Amount'], rows);
            }} data-testid="export-pnl-pdf">Export PDF</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2"><ChartLine className="w-4 h-4 text-[#00C853]" /> Revenue</h3>
              {pnl.revenue.length === 0 ? <p className="text-sm text-[#999]">No revenue entries</p> : pnl.revenue.map((r, i) => (
                <div key={i} className="flex justify-between py-1 text-sm"><span className="text-[#666]">{r.account}</span><span className="font-medium text-[#00C853]">{fmt(r.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 mt-2 border-t border-[#E0E0E0] font-bold text-sm">
                <span className="text-[#0A0A0A]">Total Revenue</span><span className="text-[#00C853]">{fmt(pnl.total_revenue)}</span>
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2"><ChartLine className="w-4 h-4 text-[#FF3333]" /> Expenses</h3>
              {pnl.expenses.length === 0 ? <p className="text-sm text-[#999]">No expense entries</p> : pnl.expenses.map((r, i) => (
                <div key={i} className="flex justify-between py-1 text-sm"><span className="text-[#666]">{r.account}</span><span className="font-medium text-[#FF3333]">{fmt(r.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 mt-2 border-t border-[#E0E0E0] font-bold text-sm">
                <span className="text-[#0A0A0A]">Total Expenses</span><span className="text-[#FF3333]">{fmt(pnl.total_expenses)}</span>
              </div>
            </Card>
          </div>
          <Card className="p-4">
            <div className="flex justify-between items-center">
              <span className="font-bold text-[#0A0A0A]">Net Profit / (Loss)</span>
              <span className={`text-xl font-bold ${pnl.net_profit >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`} data-testid="net-profit">{fmt(pnl.net_profit)}</span>
            </div>
          </Card>
        </div>
      )}

      {/* Balance Sheet */}
      {tab === 'balance-sheet' && bs && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = [...bs.assets.map(a => ['Asset', a.account, fmt(a.amount)]), ['', 'Total Assets', fmt(bs.total_assets)],
                ...bs.liabilities.map(a => ['Liability', a.account, fmt(a.amount)]), ['', 'Total Liabilities', fmt(bs.total_liabilities)],
                ...bs.equity.map(a => ['Equity', a.account, fmt(a.amount)]), ['', 'Total Equity', fmt(bs.total_equity)]];
              exportPDF('Balance Sheet', ['Category', 'Account', 'Amount'], rows);
            }} data-testid="export-bs-pdf">Export PDF</Button>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2"><Bank className="w-4 h-4 text-[#002FA7]" /> Assets</h3>
              {bs.assets.length === 0 ? <p className="text-sm text-[#999]">No asset entries</p> : bs.assets.map((a, i) => (
                <div key={i} className="flex justify-between py-1 text-sm"><span className="text-[#666]">{a.account}</span><span className="font-medium">{fmt(a.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 mt-2 border-t border-[#E0E0E0] font-bold text-sm">
                <span>Total</span><span className="text-[#002FA7]">{fmt(bs.total_assets)}</span>
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2"><Bank className="w-4 h-4 text-[#FF3333]" /> Liabilities</h3>
              {bs.liabilities.length === 0 ? <p className="text-sm text-[#999]">No liability entries</p> : bs.liabilities.map((a, i) => (
                <div key={i} className="flex justify-between py-1 text-sm"><span className="text-[#666]">{a.account}</span><span className="font-medium">{fmt(a.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 mt-2 border-t border-[#E0E0E0] font-bold text-sm">
                <span>Total</span><span className="text-[#FF3333]">{fmt(bs.total_liabilities)}</span>
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2"><Bank className="w-4 h-4 text-[#7C3AED]" /> Equity</h3>
              {bs.equity.length === 0 ? <p className="text-sm text-[#999]">No equity entries</p> : bs.equity.map((a, i) => (
                <div key={i} className="flex justify-between py-1 text-sm"><span className="text-[#666]">{a.account}</span><span className="font-medium">{fmt(a.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 mt-2 border-t border-[#E0E0E0] font-bold text-sm">
                <span>Total</span><span className="text-[#7C3AED]">{fmt(bs.total_equity)}</span>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Account Modal */}
      <Modal isOpen={showAccModal} onClose={() => setShowAccModal(false)} title="Add Account">
        <form onSubmit={handleCreateAcc} className="space-y-3" data-testid="acc-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Input label="Code" value={accForm.code} onChange={e => setAccForm({...accForm, code: e.target.value})} required data-testid="acc-code-input" />
            <Input label="Name" value={accForm.name} onChange={e => setAccForm({...accForm, name: e.target.value})} required data-testid="acc-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Type" value={accForm.account_type} onChange={e => setAccForm({...accForm, account_type: e.target.value})}>
              <option value="asset">Asset</option><option value="liability">Liability</option>
              <option value="equity">Equity</option><option value="revenue">Revenue</option><option value="expense">Expense</option>
            </Select>
            <Input label="Parent Code" value={accForm.parent_code} onChange={e => setAccForm({...accForm, parent_code: e.target.value})} placeholder="e.g. 1000" />
          </div>
          <Input label="Description" value={accForm.description} onChange={e => setAccForm({...accForm, description: e.target.value})} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowAccModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="acc-submit-btn">Create</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
